import { Gs1Config, Gs1Row } from '../views/LabelBuilder/LabelBuilder.slice'
import { Gs1CarrierMode } from '../apis/gs1.api'

/**
 * Starting points for the GS1 Barcode panel. These are only defaults the
 * user can change; every GS1 rule is checked by the API (/gs1/validate).
 * Test values match the client's prototype so previews look the same.
 */

const api = (ai: string, field: string, testValue: string): Gs1Row => ({
  ai,
  source: { kind: 'api', field: `api:${field}` },
  test_value: testValue
})

export interface Gs1Preset {
  key: string
  name: string
  rows: Gs1Row[]
}

const aiPresets: Gs1Preset[] = [
  { key: 'gtin', name: 'GTIN', rows: [api('01', 'gtin', '00812345678901')] },
  { key: 'sscc', name: 'SSCC pallet', rows: [api('00', 'sscc', '008123450000000016')] },
  {
    key: 'case',
    name: 'Case label',
    rows: [
      api('01', 'gtin', '00812345678901'),
      { ai: '17', source: { kind: 'system', field: 'currentDate', offset_days: 365 } },
      api('10', 'lot_number', 'L2291')
    ]
  },
  { key: 'serial', name: 'Serialized item', rows: [api('01', 'gtin', '00812345678901'), api('21', 'serial', 'SN000184')] },
  { key: 'weight', name: 'Variable weight', rows: [api('01', 'gtin', '90812345678904'), api('3103', 'net_weight', '001250')] },
  {
    key: 'pallet',
    name: 'Pallet with contents',
    rows: [api('00', 'sscc', '008123450000000016'), api('02', 'content_gtin', '00812345678901'), api('37', 'piece_qty', '48')]
  }
]

const digitalLinkPresets: Gs1Preset[] = [
  { key: 'prod', name: 'Product', rows: [api('01', 'gtin', '00812345678901')] },
  {
    key: 'lot',
    name: 'Product, lot and serial',
    rows: [api('01', 'gtin', '00812345678901'), api('10', 'lot_number', 'L2291'), api('21', 'serial', 'SN000184'), api('17', 'expiry', '271008')]
  },
  { key: 'sscc', name: 'Logistic unit', rows: [api('00', 'sscc', '008123450000000016')] }
]

/** Sources from the system, as the existing Content chips name them. */
export const gs1SystemSources = [
  { key: 'currentDate', name: 'Current date' },
  { key: 'createdAtDate', name: 'Created at date' },
  { key: 'autoIncrement', name: 'Auto increment' }
]

/** Date formats an API may send (PHP format strings, shown with an example). */
export const gs1DateFormats = [
  { value: 'Y-m-d', name: 'ISO 8601 (2026-12-05)' },
  { value: 'm/d/Y', name: 'US (12/05/2026)' },
  { value: 'd/m/Y', name: 'European (05/12/2026)' },
  { value: 'Ymd', name: 'Compact (20261205)' },
  { value: 'ymd', name: 'GS1 YYMMDD (261205)' }
]

export const gs1ModuleDots = [1, 2, 3, 4, 5, 6, 8, 10]

/** EPC filter values (GS1 EPC Tag Data Standard). */
export const gs1EpcFilters = [
  { value: '0', name: '0 All others' },
  { value: '1', name: '1 Point-of-sale item' },
  { value: '2', name: '2 Full case' },
  { value: '3', name: '3 Reserved' },
  { value: '4', name: '4 Inner pack' },
  { value: '5', name: '5 Reserved' },
  { value: '6', name: '6 Unit load' },
  { value: '7', name: '7 Component' }
]

const gs1BarcodeService = {
  presets (mode: Gs1CarrierMode): Gs1Preset[] {
    if (mode === 'ai') return aiPresets
    if (mode === 'dl') return digitalLinkPresets
    return []
  },

  /** A new element: GS1-128 with the Case label preset, valid from the start. */
  defaultConfig (dictionaryVersion?: string): Gs1Config {
    return {
      carrier: 'gs1-128',
      preset: 'case',
      module_dots: 4,
      bar_height_mm: 20,
      human_readable: true,
      on_invalid: 'reject',
      digital_link_domain: null,
      dictionary_version: dictionaryVersion,
      elements: aiPresets[2].rows.map(row => ({ ...row, source: { ...row.source } }))
    }
  },

  /** A row for a newly added AI, with an api: field named after its title. */
  newRow (ai: string, title: string): Gs1Row {
    const field = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `ai_${ai}`
    return { ai, source: { kind: 'api', field: `api:${field}` }, test_value: '' }
  }
}

export default gs1BarcodeService
